<template>
  <div class="fixed inset-0 flex items-center justify-center">
    <div ref="container" :style="containerStyle" :class="containerClass ?? ''">
      <slot name="overlay" />
      <div
        v-for="[layoutSectionKey, facet] in Object.entries(frame.facets)"
        :key="layoutSectionKey"
        :style="style[layoutSectionKey]"
      >
        <slot :name="layoutSectionKey" :facet="facet">
          <div
            style="
              width: 100%;
              height: 100%;
              display: flex;
              align-items: center;
              justify-content: center;
              box-sizing: border-box;
            "
          >
            {{ layoutSectionKey }}
          </div>
        </slot>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { TFrame, TLayout } from '@lucimark/shared/models/playlist'
import { frameRotationContainerStyle } from '@lucimark/shared/helpers/frameRotationContainerStyle'
import { computed, onBeforeUnmount, onMounted, ref, toRefs, unref } from 'vue'

export type TRotation = '0' | '90' | '180' | '270'
const props = defineProps<{
  frame: TFrame
  rotation?: TRotation
  containerClass?: string
}>()
const { frame, rotation, containerClass } = toRefs(props)

const container = ref<null | HTMLDivElement>(null)
const parentBBox = ref<null | DOMRect>(null)

function getLayoutSectionStyleIndex(layout: TLayout): Record<string, string> {
  const { width: maxWidth, height: maxHeight } = layout
  const sectionStyleIndex = Object.entries(layout.sections).reduce(
    (accumulator: { [sectionKey: string]: string }, [sectionKey, section]) => {
      const leftRef = section.x0
      const topRef = section.y0
      const leftAbs = maxWidth
      const topAbs = maxHeight
      const width = `${(section.width / maxWidth) * 100}%`
      const height = `${(section.height / maxHeight) * 100}%`
      const left = `${(leftRef / leftAbs) * 100}%`
      const top = `${(topRef / topAbs) * 100}%`
      const style = `position:absolute; top:${top}; left:${left}; width:${width}; height:${height};`
      accumulator[sectionKey] = style
      return accumulator
    },
    {}
  )
  return sectionStyleIndex
}

const containerStyle = computed(() => {
  const bbox = unref(parentBBox)
  return frameRotationContainerStyle(bbox, unref(frame).layout, unref(rotation) ?? '0')
})

const style = computed(() => getLayoutSectionStyleIndex(unref(frame).layout))

const resizeObserver = new ResizeObserver(([entry]) => {
  parentBBox.value = entry.target.getBoundingClientRect()
})

onMounted(() => {
  const parentEl = unref(container)?.parentElement ?? null
  if (parentEl === null) {
    throw new Error('invalid parent')
  }
  resizeObserver.observe(parentEl)
})

onBeforeUnmount(() => {
  resizeObserver.disconnect()
})

defineExpose({ container })
</script>
